import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CreateUserRequest,
  Role,
  UpdateUserProfileRequest,
  UserItem,
  UserStatus,
} from '@mvcp/contracts';
import {
  createUser,
  deactivateUser,
  listCompanies,
  listUsers,
  reactivateUser,
  reassignUserCompany,
  resendInvitation,
  resetUserPassword,
  revokeInvitation,
  updateUserProfile,
} from '../api';
import { MAX_EMAIL_LEN, MAX_REASON_LEN, MAX_TEXT_LEN } from '../lib/field-limits';
import { ConfirmModal } from './ConfirmModal';

import { Avatar } from './Avatar';
/**
 * Role filter options + labels. Inlined (the web consumes `@mvcp/contracts` as TYPES
 * ONLY — a runtime import breaks `vite build`); kept byte-equal to the contract `ROLES`
 * by `enum-sync.test.ts`.
 */
export const ROLE_OPTIONS = [
  'system_administrator',
  'compliance_manager',
  'vendor',
] as const;

/** Status filter options — kept byte-equal to the contract `USER_STATUSES` by enum-sync. */
export const USER_STATUS_OPTIONS = ['active', 'invited', 'deactivated'] as const;

const ROLE_LABEL: Record<Role, string> = {
  system_administrator: 'Administrator',
  compliance_manager: 'Manager',
  vendor: 'Vendor',
};

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  deactivated: 'Deactivated',
};

/** Status pill tone: active=ok, invited=warn, deactivated=bad. */
const STATUS_TONE: Record<UserStatus, string> = {
  active: 'ok',
  invited: 'warn',
  deactivated: 'bad',
};

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

/** Where a user belongs, for the roster's context column. */
function boundTo(u: UserItem): string {
  if (u.companyName) return u.companyName;
  if (u.companyId) return u.companyId;
  if (u.vendorId) return `Vendor ${u.vendorId.slice(0, 8)}`;
  return '—';
}

/**
 * Display name (Story 6.6a): first + last when known, else the email local-part.
 * Derived client-side — never a stored field.
 */
function displayName(u: UserItem): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  return u.email.split('@')[0] || u.email;
}

/**
 * Last-active cell (Story 6.6a AC2): the login date, "Never" for a real user who
 * has never signed in, and an em-dash for a pending invite (no account yet).
 */
function lastActive(u: UserItem): string {
  if (u.lastLoginAt) return shortDate(u.lastLoginAt);
  return u.status === 'invited' ? '—' : 'Never';
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: UserItem[] }
  | { kind: 'error'; message: string };

type PendingAction =
  | { kind: 'deactivate'; user: UserItem }
  | { kind: 'reactivate'; user: UserItem }
  | { kind: 'revoke'; user: UserItem }
  | { kind: 'resend'; user: UserItem }
  | { kind: 'reset'; user: UserItem }
  | { kind: 'edit'; user: UserItem }
  | { kind: 'reassign'; user: UserItem }
  | { kind: 'create' };

/** A Company option for the reassign selector (id + display name). */
interface CompanyOption {
  id: string;
  name: string;
}

interface EditForm {
  firstName: string;
  lastName: string;
  phone: string;
}

/** Add-user form (Story 6.6b): a direct create, NOT an invite. Scope shown by role. */
interface CreateForm {
  email: string;
  role: Role;
  companyId: string;
  vendorId: string;
}

const EMPTY_CREATE_FORM: CreateForm = {
  email: '',
  role: 'compliance_manager',
  companyId: '',
  vendorId: '',
};

/**
 * Admin Console → Users (Story 6.5, FR-29/FR-30). The System-Administrator lifecycle
 * surface: lists every user across all Companies and the Vendor Directory (active,
 * deactivated, pending-invite — incl. Keycloak-backfilled admins), filterable by role
 * and status and searchable by email, with per-row lifecycle actions —
 * Deactivate/Reactivate for a real user, Resend/Revoke for a pending invitation. Mirrors
 * {@link AdministratorsView} (the golden ViewState/load template) + the
 * {@link VerifiedCertificatesView} destructive-confirm shape (`ConfirmModal`). Every
 * mutation refreshes via `load()` OUTSIDE its try/catch. System-Administrator-only (belt
 * with the API guard).
 */
export function UsersView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | UserStatus>('all');
  const [search, setSearch] = useState('');

  const [action, setAction] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [editForm, setEditForm] = useState<EditForm>({ firstName: '', lastName: '', phone: '' });
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  // Reassign-company (Story 6.6c): the chosen target Company + the option list, fetched
  // lazily when the reassign modal opens (reusing the existing companies endpoint).
  const [reassignCompanyId, setReassignCompanyId] = useState('');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resendLink, setResendLink] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const items = await listUsers();
      setState({ kind: 'ready', items });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const q = search.trim().toLowerCase();
    return state.items.filter(
      (u) =>
        (roleFilter === 'all' || u.role === roleFilter) &&
        (statusFilter === 'all' || u.status === statusFilter) &&
        (q === '' || u.email.toLowerCase().includes(q)),
    );
  }, [state, roleFilter, statusFilter, search]);

  function openAction(next: PendingAction): void {
    setAction(next);
    setReason('');
    setActionError(null);
    if (next.kind === 'edit') {
      // Prefill with the current values; on save we transmit only the fields that
      // actually changed vs these originals (see onConfirm), so an unchanged — or
      // never-loaded/blank — field is never sent and can't clear real Keycloak data.
      setEditForm({
        firstName: next.user.firstName ?? '',
        lastName: next.user.lastName ?? '',
        phone: next.user.phone ?? '',
      });
    }
    if (next.kind === 'create') {
      setCreateForm(EMPTY_CREATE_FORM);
    }
    if (next.kind === 'reassign') {
      // Reset the selection and lazily fetch the Company options. A load failure leaves
      // the list empty (the modal shows "no other companies"); it does not crash the view.
      setReassignCompanyId('');
      setCompanies([]);
      void listCompanies()
        .then((list) => setCompanies(list.map((c) => ({ id: c.id, name: c.name }))))
        .catch(() => setCompanies([]));
    }
  }

  async function onConfirm(): Promise<void> {
    if (!action) return;
    // Deactivate requires a reason (recorded in the audit log).
    if (action.kind === 'deactivate' && reason.trim().length === 0) {
      setActionError('A reason is required — it is recorded in the audit log.');
      return;
    }
    setActionError(null);
    setBusy(true);
    let done = false;
    let link: string | null = null;
    let doneNotice: string | null = null;
    try {
      switch (action.kind) {
        case 'deactivate':
          await deactivateUser(action.user.id, reason.trim());
          doneNotice = `Deactivated ${action.user.email}`;
          break;
        case 'reactivate':
          await reactivateUser(action.user.id);
          doneNotice = `Reactivated ${action.user.email}`;
          break;
        case 'revoke':
          await revokeInvitation(action.user.id);
          doneNotice = `Revoked the invitation for ${action.user.email}`;
          break;
        case 'resend': {
          const inv = await resendInvitation(action.user.id);
          link = inv.activationLink ?? null;
          doneNotice = `A fresh invitation was sent to ${action.user.email}`;
          break;
        }
        case 'reset':
          await resetUserPassword(action.user.id);
          doneNotice = `Sent a password-reset email to ${action.user.email}`;
          break;
        case 'create': {
          // Direct create (NOT an invite): email + role + role-appropriate scope. The
          // server never returns a password; the new user gets a set-password email.
          const email = createForm.email.trim();
          if (!email) {
            setActionError('An email is required.');
            return;
          }
          const input: CreateUserRequest = { email, role: createForm.role };
          if (createForm.role === 'compliance_manager') {
            const companyId = createForm.companyId.trim();
            if (!companyId) {
              setActionError('A company ID is required for a manager.');
              return;
            }
            input.companyId = companyId;
          } else if (createForm.role === 'vendor') {
            const vendorId = createForm.vendorId.trim();
            if (!vendorId) {
              setActionError('A vendor ID is required for a vendor user.');
              return;
            }
            input.vendorId = vendorId;
          }
          const created = await createUser(input);
          doneNotice = `Created ${created.email}`;
          break;
        }
        case 'reassign': {
          // Rebind the manager to the chosen Company. A selection is required; the
          // current Company is excluded from the options (same-company is a server 409).
          const companyId = reassignCompanyId.trim();
          if (!companyId) {
            setActionError('Choose a company to reassign to.');
            return;
          }
          const updated = await reassignUserCompany(action.user.id, companyId);
          doneNotice = `Reassigned ${updated.email} to ${updated.companyName ?? updated.companyId ?? 'the selected company'}`;
          break;
        }
        case 'edit': {
          // Send ONLY the fields the admin actually changed. Comparing against the
          // prefilled originals means an untouched field is never transmitted — so a
          // row loaded without realm metadata (a Keycloak snapshot outage or the
          // listRealmUsers max cap → blank prefill) can't silently PUT an empty string
          // that WIPES the real Keycloak first/last name or phone. Intentional clears
          // still flow: a field emptied from a non-empty original differs, so it is
          // sent as ''. Nothing changed → no request (avoids a phantom audit row + a
          // spurious 400), just a notice.
          const patch: UpdateUserProfileRequest = {};
          const first = editForm.firstName.trim();
          const last = editForm.lastName.trim();
          const phone = editForm.phone.trim();
          // Compare trimmed-vs-trimmed: a stored value with incidental whitespace
          // (" Ada") must not make an untouched field read as "changed".
          if (first !== (action.user.firstName ?? '').trim()) patch.firstName = first;
          if (last !== (action.user.lastName ?? '').trim()) patch.lastName = last;
          if (phone !== (action.user.phone ?? '').trim()) patch.phone = phone;
          if (Object.keys(patch).length === 0) {
            setActionError('No changes to save.');
            return;
          }
          await updateUserProfile(action.user.id, patch);
          doneNotice = `Updated the profile for ${action.user.email}`;
          break;
        }
      }
      done = true;
      setAction(null);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "Couldn't complete. Try again.");
    } finally {
      setBusy(false);
    }
    // Refresh + surface the outcome OUTSIDE the mutation try/catch, so a refresh
    // failure never turns a successful action into an error.
    if (done) {
      setResendLink(link);
      setNotice(doneNotice);
      await load();
    }
  }

  const modal = action
    ? actionModal(
        action,
        reason,
        setReason,
        editForm,
        setEditForm,
        createForm,
        setCreateForm,
        companies,
        reassignCompanyId,
        setReassignCompanyId,
      )
    : null;

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Users</h1>
        <p className="muted" style={{ margin: 0 }}>
          Every user across all companies and the vendor directory. Deactivate or reactivate access,
          and resend or revoke a pending invitation. Every change is logged.
        </p>
      </div>

      <section className="card stack" aria-labelledby="users-heading">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <h2 id="users-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Platform users
          </h2>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => openAction({ kind: 'create' })}
          >
            Add user
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Role</span>
            <select
              aria-label="Filter by role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as 'all' | Role)}
              style={fieldStyle}
            >
              <option value="all">All roles</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Status</span>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | UserStatus)}
              style={fieldStyle}
            >
              <option value="all">All statuses</option>
              {USER_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
            <span className="field-label">Search email</span>
            <input
              aria-label="Search email"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="name@example.com"
              style={fieldStyle}
            />
          </label>
        </div>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && (
          <div className="stack" style={{ gap: 8 }} role="alert">
            <p className="muted" style={{ margin: 0 }}>{state.message}</p>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => void load()}
              style={{ alignSelf: 'flex-start' }}
            >
              Retry
            </button>
          </div>
        )}

        {state.kind === 'ready' && filtered.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No users match these filters.</p>
        )}

        {state.kind === 'ready' && filtered.length > 0 && (
          /* `.table-scroll` + `.data-table` (global.css): the widest table in the app —
             eight columns, four row actions — set at the shared roster density, with
             identifiers and dates held on one line. The wrapper is not optional: the
             nowrap cells raise the table's min-content width past the 1024px content
             pane, and without it that overflow becomes a horizontally-scrolling PAGE. */
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Belongs to</th>
                  <th>Status</th>
                  <th>Since</th>
                  <th>Last active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={`${u.id}:${u.status}`} data-user-row={u.email}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <Avatar id={u.id} name={u.email} />
                        <span data-numeric>{u.email}</span>
                      </span>
                    </td>
                    <td data-name={u.email}>{u.status === 'invited' ? '—' : displayName(u)}</td>
                    <td>{ROLE_LABEL[u.role]}</td>
                    <td>{boundTo(u)}</td>
                    <td>
                      <span className="status-pill" data-tone={STATUS_TONE[u.status]}>
                        <span className="status-pill__dot" aria-hidden="true" />
                        {STATUS_LABEL[u.status]}
                      </span>
                    </td>
                    <td data-numeric>{shortDate(u.createdAt)}</td>
                    <td data-numeric data-last-active={u.email}>
                      {lastActive(u)}
                    </td>
                    {/* Every action this row offers, always all of them, never an overflow
                        menu: the set is status-dependent (see the guards below) and each
                        control's visible text is also its accessible name. `.row-actions`
                        folds them to a 2×2 block when four are present — see global.css for
                        why they cannot share one line at 1340px. */}
                    <td className="data-table__actions">
                      <div className="row-actions">
                        {u.status !== 'invited' && (
                          <button
                            type="button"
                            className="btn-inline"
                            onClick={() => openAction({ kind: 'edit', user: u })}
                          >
                            Edit
                          </button>
                        )}
                        {u.status === 'active' && (
                          <button
                            type="button"
                            className="btn-inline"
                            onClick={() => openAction({ kind: 'reset', user: u })}
                          >
                            Reset password
                          </button>
                        )}
                        {u.status === 'active' && u.role === 'compliance_manager' && (
                          <button
                            type="button"
                            className="btn-inline"
                            onClick={() => openAction({ kind: 'reassign', user: u })}
                          >
                            Reassign company
                          </button>
                        )}
                        {u.status === 'active' && (
                          <button
                            type="button"
                            className="btn-inline"
                            data-tone="bad"
                            onClick={() => openAction({ kind: 'deactivate', user: u })}
                          >
                            Deactivate
                          </button>
                        )}
                        {u.status === 'deactivated' && (
                          <button
                            type="button"
                            className="btn-inline"
                            onClick={() => openAction({ kind: 'reactivate', user: u })}
                          >
                            Reactivate
                          </button>
                        )}
                        {u.status === 'invited' && (
                          <>
                            <button
                              type="button"
                              className="btn-inline"
                              onClick={() => openAction({ kind: 'resend', user: u })}
                            >
                              Resend
                            </button>
                            <button
                              type="button"
                              className="btn-inline"
                              data-tone="bad"
                              onClick={() => openAction({ kind: 'revoke', user: u })}
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
          {notice && (
            <p className="muted" style={{ margin: 0, fontSize: 13, wordBreak: 'break-all' }}>
              {notice}.
              {resendLink && (
                <>
                  {' '}
                  Activation link (dev — no email sent): <span data-numeric>{resendLink}</span>
                </>
              )}
            </p>
          )}
        </div>
      </section>

      {modal && (
        <ConfirmModal
          title={modal.title}
          message={modal.message}
          confirmLabel={modal.confirmLabel}
          busy={busy}
          error={actionError}
          onConfirm={() => void onConfirm()}
          onCancel={() => setAction(null)}
        />
      )}
    </>
  );
}

/** Build the ConfirmModal copy for a pending action (deactivate carries a reason field;
 * edit carries a first/last/phone form + a read-only MFA indicator; create carries the
 * Add-user form; reset is a plain confirm). */
function actionModal(
  action: PendingAction,
  reason: string,
  setReason: (v: string) => void,
  editForm: EditForm,
  setEditForm: (v: EditForm) => void,
  createForm: CreateForm,
  setCreateForm: (v: CreateForm) => void,
  companies: CompanyOption[],
  reassignCompanyId: string,
  setReassignCompanyId: (v: string) => void,
): { title: string; message: React.ReactNode; confirmLabel: string } {
  switch (action.kind) {
    case 'create':
      return {
        title: 'Add user',
        confirmLabel: 'Create user',
        message: (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              Create a user directly. They are provisioned immediately and emailed a link to
              set their own password — you never see or set a password. This action is logged.
            </p>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Email</span>
              <input
                aria-label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="name@example.com"
                maxLength={MAX_EMAIL_LEN}
                style={fieldStyle}
              />
            </label>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Role</span>
              <select
                aria-label="Role"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as Role })}
                style={fieldStyle}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </label>
            {createForm.role === 'compliance_manager' && (
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Company ID</span>
                <input
                  aria-label="Company ID"
                  value={createForm.companyId}
                  onChange={(e) => setCreateForm({ ...createForm, companyId: e.target.value })}
                  placeholder="Company UUID"
                  style={fieldStyle}
                />
              </label>
            )}
            {createForm.role === 'vendor' && (
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Vendor ID</span>
                <input
                  aria-label="Vendor ID"
                  value={createForm.vendorId}
                  onChange={(e) => setCreateForm({ ...createForm, vendorId: e.target.value })}
                  placeholder="Vendor UUID"
                  style={fieldStyle}
                />
              </label>
            )}
          </div>
        ),
      };
    case 'reset':
      return {
        title: 'Reset password',
        confirmLabel: 'Send reset email',
        message: (
          <p style={{ margin: 0 }}>
            This sends a set-a-new-password email to <strong>{action.user.email}</strong>. They
            choose their own password — you never see or set it. This action is logged.
          </p>
        ),
      };
    case 'edit':
      return {
        title: 'Edit profile',
        confirmLabel: 'Save',
        message: (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              Update the profile for <strong>{action.user.email}</strong>. Email, role, and
              company/vendor are not editable here. This action is logged.
            </p>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">First name</span>
              <input
                aria-label="First name"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Last name</span>
              <input
                aria-label="Last name"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Contact phone</span>
              <input
                aria-label="Contact phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                maxLength={MAX_TEXT_LEN}
                style={fieldStyle}
              />
            </label>
            <p className="muted" style={{ margin: 0, fontSize: 13 }} data-mfa-indicator>
              MFA configured:{' '}
              {action.user.mfaEnabled === undefined
                ? '—'
                : action.user.mfaEnabled
                  ? 'Yes'
                  : 'No'}
            </p>
          </div>
        ),
      };
    case 'reassign': {
      // Offer every Company EXCEPT the user's current one (the same-company case is a
      // server-side 409 — don't surface it as a choice).
      const options = companies.filter((c) => c.id !== action.user.companyId);
      return {
        title: 'Reassign company',
        confirmLabel: 'Reassign',
        message: (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              Move <strong>{action.user.email}</strong> to a different company. Their scope
              changes on their next sign-in — active sessions are ended now. This action is
              logged.
            </p>
            {options.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                No other company is available to reassign to.
              </p>
            ) : (
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Company</span>
                <select
                  aria-label="Company"
                  value={reassignCompanyId}
                  onChange={(e) => setReassignCompanyId(e.target.value)}
                  style={fieldStyle}
                >
                  <option value="">Select a company…</option>
                  {options.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        ),
      };
    }
    case 'deactivate':
      return {
        title: 'Deactivate user',
        confirmLabel: 'Deactivate',
        message: (
          <div className="stack" style={{ gap: 8 }}>
            <p style={{ margin: 0 }}>
              This disables sign-in for <strong>{action.user.email}</strong> and ends their active
              sessions. It is reversible — you can reactivate them later. This action is logged.
            </p>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Reason</span>
              <textarea
                aria-label="Deactivation reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={MAX_REASON_LEN}
                style={{
                  padding: '8px 10px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rounded-md)',
                  color: 'var(--text)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </label>
          </div>
        ),
      };
    case 'reactivate':
      return {
        title: 'Reactivate user',
        confirmLabel: 'Reactivate',
        message: (
          <p style={{ margin: 0 }}>
            This restores sign-in for <strong>{action.user.email}</strong>. This action is logged.
          </p>
        ),
      };
    case 'revoke':
      return {
        title: 'Revoke invitation',
        confirmLabel: 'Revoke',
        message: (
          <p style={{ margin: 0 }}>
            This cancels the pending invitation for <strong>{action.user.email}</strong>; their
            activation link will stop working. This action is logged.
          </p>
        ),
      };
    case 'resend':
      return {
        title: 'Resend invitation',
        confirmLabel: 'Resend',
        message: (
          <p style={{ margin: 0 }}>
            This sends a fresh activation link to <strong>{action.user.email}</strong> and
            invalidates the previous one. This action is logged.
          </p>
        ),
      };
  }
}
